import type {
  GetStackFramesForRunKeyPreviewResponse,
  GetStackFramesForRunKeyResponse,
  StackFrameType,
} from '@cotera/api';
import { invalidateStackFrameFetchCacheForRunKey } from '@cotera/client/app/hooks/stack-frame-fetch';
import { ATC, DG } from '@cotera/sdk/core';
import { err, ok, type Result } from 'neverthrow';
import {
  PollingWatchable,
  ReadonlyPollingWatchable,
  ReadonlyWatchable,
  TargetableModel,
  Watchable,
} from '../../actions/framework';
import { BaseModel } from '../../actions/framework/model.base';
import { Ty } from '@cotera/era';
import { v4 } from 'uuid';

/** null = loading; Result = fetched data or error */
export type StackFramesWatchableValue = Result<
  GetStackFramesForRunKeyResponse | null,
  unknown
> | null;

export const hasWarehouseValue = (value: unknown): boolean =>
  value !== null && value !== undefined;

const TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'cancelled',
  'stalled',
]);

const hasIncompleteFrames = (response: {
  frames: Array<{ status: string }>;
}): boolean => {
  return response.frames.some((frame) => !TERMINAL_STATUSES.has(frame.status));
};

/** Map preview response to full shape expected by consumers */
export const mapPreviewToFull = (
  preview: GetStackFramesForRunKeyPreviewResponse | null
): GetStackFramesForRunKeyResponse | null => {
  if (preview === null) {
    return null;
  }
  const f = preview.frame;
  const frames: GetStackFramesForRunKeyResponse['frames'] =
    f === null
      ? []
      : [
          {
            id: f.id,
            parentId: null,
            createdAt: new Date(0),
            status: f.status,
            depth: f.depth,
            position: f.position,
            input: null,
            output: f.output,
            fpType: f.fpType,
            displayName: f.displayName,
            tags: [],
            retries: 0,
            events: [],
            startedAt: null,
            endedAt: null,
            allocatedAt: null,
            cancelledAt: null,
          },
        ];
  return {
    runId: '',
    orgId: '',
    runKey: '',
    tags: [],
    heartbeatAt: null,
    uddWriteKey: undefined,
    frames,
  };
};

/** Slim format for cache - only fields used by the UI */
type CachedFrame = Pick<
  StackFrameType,
  'id' | 'status' | 'depth' | 'position' | 'output' | 'fpType' | 'displayName'
>;
type CachedStackFramesResponse = { frames: CachedFrame[] };

const slimForCache = (
  full: GetStackFramesForRunKeyResponse
): CachedStackFramesResponse => ({
  frames: full.frames.map((f) => ({
    id: f.id,
    status: f.status,
    depth: f.depth,
    position: f.position,
    output: f.output,
    fpType: f.fpType,
    displayName: f.displayName,
  })),
});

/** Expand cached slim format to full response shape (consumers only use .frames) */
const expandCached = (
  cached: Result<CachedStackFramesResponse | null, unknown>
): StackFramesWatchableValue => {
  if (cached.isErr()) {
    return err(cached.error);
  }
  const v = cached.value;
  if (v === null) {
    return ok(null);
  }
  const frames: StackFrameType[] = v.frames.map((f) => ({
    id: f.id,
    parentId: null,
    createdAt: new Date(0),
    status: f.status,
    depth: f.depth,
    position: f.position,
    input: null,
    output: f.output,
    fpType: f.fpType ?? '',
    displayName: f.displayName ?? '',
    tags: [],
    retries: 0,
    events: [],
    startedAt: null,
    endedAt: null,
    allocatedAt: null,
    cancelledAt: null,
  }));
  return ok({
    runId: '',
    orgId: '',
    runKey: '',
    tags: [],
    heartbeatAt: null,
    uddWriteKey: undefined,
    frames,
  });
};

/** True if result is terminal (completed/error) and safe to cache and stop polling */
const isTerminalResult = (value: StackFramesWatchableValue): boolean => {
  if (value === null) {
    return false;
  }
  if (value.isErr()) {
    return true;
  }
  const v = value.value;
  if (v === null) {
    return false;
  }
  return !hasIncompleteFrames(v);
};

/** Max age (ms) for in-progress cached data before we refetch. Terminal results never expire until invalidated. */
const CACHE_FRESHNESS_MS = 5000;

type CacheEntry = {
  value: Result<CachedStackFramesResponse | null, unknown>;
  timestamp: number;
};

/** Cache keyed by runKey - persists across model lifecycle so scroll-out-then-back reuses data without refetch */
const stackFramesCache = new Map<string, CacheEntry>();

export class AtcCellModel extends BaseModel implements TargetableModel {
  private _isTargeted: boolean = false;
  private readonly _visibleRef = { current: false };
  private _forcePolling = false;
  readonly stackFramesWatchable: ReadonlyPollingWatchable<StackFramesWatchableValue>;

  readonly columnConfig: ReadonlyWatchable<DG.Graph.ColConfig | null>;
  readonly startingIdentity: ReadonlyWatchable<DG.Graph.Identity>;
  readonly runKey: ReadonlyWatchable<string | null>;
  readonly fetchKey: Watchable<string> = Watchable.fromValue(v4());
  readonly relIdentity: ReadonlyWatchable<DG.Graph.Identity>;
  readonly colIdentity: ReadonlyWatchable<DG.Graph.Identity>;
  readonly stableId: ReadonlyWatchable<string>;

  setVisible(visible: boolean): void {
    const wasVisible = this._visibleRef.current;
    this._visibleRef.current = visible;
    if (visible && !wasVisible) {
      if (this.shouldSkipPolling()) {
        return;
      }
      this.stackFramesWatchable.restart();
    }
  }

  private shouldSkipPolling(): boolean {
    return hasWarehouseValue(this.warehouseValue) && !this._forcePolling;
  }

  restartStackFramesPolling(): void {
    this._forcePolling = true;
    const rk = this.runKey.snapshot();
    if (rk !== null) {
      stackFramesCache.delete(rk);
      invalidateStackFrameFetchCacheForRunKey(rk);
    }
    this.stackFramesWatchable.restart();
  }

  constructor(
    readonly rowIndex: number,
    readonly columnName: string,
    readonly relName: string,
    stableId: string | undefined,
    private readonly _llmConfig: {
      model: string;
      when: 'never' | 'daily' | 'hourly' | { cron: string };
      temperature: number;
      maxOutputTokens: number;
      topP: number;
      topK: number;
      frequencyPenalty: number;
      presencePenalty: number;
    },
    private readonly getEditorState: () => DG.DocumentEditor.DocumentState,
    private readonly graph: ReadonlyWatchable<DG.BuildExplorer>,
    readonly isPreviewMode: boolean = false,
    private readonly fetchStackFrames: (
      runKey: string
    ) => Promise<Result<GetStackFramesForRunKeyResponse | null, unknown>>,
    readonly warehouseValue: unknown
  ) {
    super(`atc-cell-${relName}-${columnName}-${rowIndex}`);

    const tempRelOpNumber = Math.floor(100000 + Math.random() * 900000);
    const tempOpNumber = Math.floor(100000 + Math.random() * 900000);

    const getTempColIdentity = (graph: DG.BuildExplorer): DG.Graph.Identity => {
      return DG.Graph.Identity.forOperation(graph.commits.length, tempOpNumber);
    };

    const getTempRelIdentity = (graph: DG.BuildExplorer): DG.Graph.Identity => {
      return DG.Graph.Identity.forOperation(
        graph.commits.length,
        tempRelOpNumber
      );
    };

    const initialGraph = this.graph.snapshot();
    this.startingIdentity = Watchable.fromValue(
      initialGraph.getColumn({
        rel: this.relName,
        name: this.columnName,
      })?.meta.identity ?? getTempColIdentity(initialGraph)
    );

    this.columnConfig = Watchable.from((get) => {
      const graph = get(this.graph);

      return (
        graph.getColumn({
          rel: this.relName,
          name: this.columnName,
        })?.config ?? null
      );
    });

    this.relIdentity = Watchable.from((get) => {
      const graph = get(this.graph);

      return (
        graph.getRel({ symbol: this.relName })?.rawNode.meta.identity ??
        getTempRelIdentity(graph)
      );
    });

    this.colIdentity = Watchable.from((get) => {
      const graph = get(this.graph);

      return (
        graph.getColumn({
          rel: this.relName,
          name: this.columnName,
        })?.meta.identity ?? getTempColIdentity(graph)
      );
    });

    this.stableId = Watchable.fromValue(
      stableId ?? `${this.relName}-${this.columnName}-${this.rowIndex}`
    );

    this.runKey = Watchable.from((get) => {
      return ATC.RunKeyHelpers.derive({
        t: 'workflow-program-row',
        relIdentity: get(this.relIdentity).toString(),
        colIdentity: get(this.colIdentity).toString(),
        stableId: get(this.stableId),
      });
    });

    const initialRunKey = this.runKey.snapshot();
    const skipPollingInitially = this.shouldSkipPolling();
    const cachedInitial = (() => {
      if (skipPollingInitially) {
        return ok(null);
      }
      if (initialRunKey === null) {
        return null;
      }
      const cachedEntry = stackFramesCache.get(initialRunKey);
      if (cachedEntry === undefined) {
        return null;
      }
      const expanded = expandCached(cachedEntry.value);
      if (!isTerminalResult(expanded)) {
        const age = Date.now() - cachedEntry.timestamp;
        if (age >= CACHE_FRESHNESS_MS) {
          return null; // In-progress cache too stale, will fetch
        }
      }
      return expanded;
    })();

    this.stackFramesWatchable = PollingWatchable.create(
      async (get): Promise<StackFramesWatchableValue> => {
        if (!this._visibleRef.current) {
          return null;
        }
        if (this.shouldSkipPolling()) {
          return ok(null);
        }
        const rk = get(this.runKey);
        if (rk === null) {
          return null;
        }
        const cachedEntry = stackFramesCache.get(rk);
        if (cachedEntry !== undefined) {
          const expanded = expandCached(cachedEntry.value);
          if (isTerminalResult(expanded)) {
            return expanded; // Terminal: use cache, no fetch
          }
          const age = Date.now() - cachedEntry.timestamp;
          if (age < CACHE_FRESHNESS_MS) {
            return expanded; // In-progress but fresh: use cache, skip fetch
          }
        }
        const result = await this.fetchStackFrames(rk);
        if (
          result.isOk() &&
          result.value !== null &&
          !hasIncompleteFrames(result.value)
        ) {
          this._forcePolling = false;
        }
        const toCache =
          result.isOk() && result.value !== null
            ? ok(slimForCache(result.value))
            : result;
        stackFramesCache.set(rk, { value: toCache, timestamp: Date.now() });
        return result;
      },
      {
        initialValue: cachedInitial,
        intervalMs: 4000,
        stopWhen: (value): boolean => {
          if (!this._visibleRef.current) {
            return true;
          }
          if (this.shouldSkipPolling()) {
            return true;
          }
          if (value === null) {
            return false;
          }
          if (value.isErr()) {
            return true;
          }
          const v = value.value;

          if (v === null) {
            // No active run — keep polling only while waiting for a run after play.
            return !this._forcePolling;
          }
          return !hasIncompleteFrames(v);
        },
      }
    );
  }

  dispose(): void {
    this.stackFramesWatchable.unsubscribe();
  }

  get isTargeted() {
    return this._isTargeted;
  }

  target(): void {
    this._isTargeted = true;
  }

  markAsNotTargeted(): void {
    this._isTargeted = false;
  }

  get llmConfig() {
    const editorState = this.getEditorState();
    const docEditor = new DG.DocumentEditor.EditorState(editorState);
    const formState = this._llmConfig;

    return {
      t: 'llm' as const,
      version: 'v2' as const,
      outputTy: Ty.displayTy(docEditor.getOutputTy()),
      editorState,
      model: formState.model,
      schedule: formState.when,
      temperature: formState.temperature,
      maxOutputTokens: formState.maxOutputTokens,
      topP: 1,
      topK: 1,
      frequencyPenalty: 0,
      presencePenalty: 0,
    };
  }
}
